/**
 * This is the main entrypoint file for the application.
 *
 * When loaded in the client side, the application is rendered in the
 * #root element.
 *
 * When the bundle created from this file is imported in the server
 * side, the exported `renderApp` function can be used for server side
 * rendering.
*
* Note that this file is required for the build process.
*/

// Note: We avoid unconditional legacy polyfills to reduce TBT and JS payload.
// Legacy polyfills are conditionally loaded for older browsers later in this file.

// `useBuiltIns: 'entry'` (see babel-preset-react-app/create.js, corejs: 3) requires this
// exact import in the true webpack entry file. Babel replaces it at build time with only
// the polyfills the `browserslist.production` targets actually lack (Promise.allSettled,
// Array.prototype.flat/flatMap, Object.fromEntries, etc.) — without it, NO runtime built-in
// polyfills are ever injected, regardless of the browserslist floor. This was missing, which
// is why Safari 12.1/11.1 still broke after the browserslist floor was widened: that only
// affects Babel's *syntax* transforms (optional chaining, etc.), not missing built-ins like
// `Promise.allSettled` — called directly in fetchAppAssets (src/ducks/hostedAssets.duck.js),
// which runs before the first render and has no fallback on browsers that lack it.
// Note: no `regenerator-runtime/runtime` import alongside this — our Safari >= 11 floor has
// native async/await and generators, so Babel never engages `transform-regenerator` for this
// target (confirmed via `@babel/preset-env`'s `debug: true` output), and regenerator-runtime
// isn't a direct dependency of this project (only pulled in transitively by other packages).
import 'core-js/stable';

// Dependency libs
import React from 'react';
import ReactDOM from 'react-dom';
import { loadableReady } from '@loadable/component';

// Import default styles before other CSS-related modules are imported
// This ensures that the styles in marketplaceDefaults.css are included
// as first ones in the final build CSS build file.
import './styles/marketplaceDefaults.css';

// Plain (non-CSS-module) stylesheets must be imported from the entry for the same reason.
// Their class names are not hashed, so whether they beat a CSS-module rule on the same element
// is decided purely by load order. Imported only from a component, they land in that route's
// chunk stylesheet, which loads *after* main.css — e.g. SkeletonCard.css's global
// `.skeleton-card` background then overrode the module rule it had always lost to, flipping the
// skeleton card from transparent to white. Importing here keeps them in main.css, first, where
// module rules can still override them.
import './components/SkeletonCard/SkeletonCard.css';

// Configs and store setup
import appSettings from './config/settings';
import defaultConfig from './config/configDefault';
import { LoggingEventsHandler, EventsHandler } from './analytics/handlers';
import configureStore from './store';

// Utils
import { createInstance, types as sdkTypes } from './util/sdkLoader';
import { mergeConfig } from './util/configHelpers';
import { matchPathname } from './util/routes';
import * as apiUtils from './util/api';
import * as log from './util/log';

// Import relevant global duck files
import { authInfo } from './ducks/auth.duck';
import { fetchAppAssets } from './ducks/hostedAssets.duck';
import { fetchCurrentUser, setCUserCurrencyAndLocation } from './ducks/user.duck';

// Route config
import routeConfiguration from './routing/routeConfiguration';
// App it self
import { ClientApp, renderApp } from './app';
import { fetchCartItems } from './ducks/cart.duck';
// import { initiateIntercomForLoggnedUser } from './util/intercom';

// Performance optimization: Lazy load non-critical modules
const loadPolyfills = () => import('./util/polyfills');

// Load legacy polyfills only for very old browsers
const ensureLegacyPolyfills = () => {
  // Synchronous Intl.Locale shim for Safari ≤ 13 (e.g. macOS High Sierra stuck at Safari 13.1.2).
  // intl-messageformat v9+ calls `new Intl.Locale(tag)` on every formatMessage invocation.
  // Intl.Locale was only added in Safari 14 (Sept 2020); without this shim every FormattedMessage
  // throws and react-intl falls back to rendering the raw ICU string — e.g. "{title}",
  // "{feeType, select, …}" — instead of the resolved value.
  // This MUST be synchronous and run here (before render()) because the polyfills.js chunk
  // loads in parallel with app init and may arrive after the first React render.
  if (typeof Intl !== 'undefined' && !Intl.Locale) {
    // eslint-disable-next-line no-inner-declarations
    function IntlLocale(tag) {
      this._tag = typeof tag === 'string' ? tag : String(tag);
    }
    IntlLocale.prototype.toString = function () { return this._tag; };
    IntlLocale.prototype.toJSON = function () { return this._tag; };
    Object.defineProperty(IntlLocale.prototype, 'baseName', {
      get: function () { return this._tag.split('-u-')[0]; },
    });
    Intl.Locale = IntlLocale;
  }

  // Synchronous Promise.allSettled shim for Safari < 13 (e.g. Safari 12.1/Mojave,
  // Safari 11.1/High Sierra). Promise.allSettled is called unguarded and unawaited-for-
  // fallback in fetchAppAssets (src/ducks/hostedAssets.duck.js) and elsewhere, dispatched
  // from render() below *before* the first ReactDOM render — without this, the resulting
  // TypeError aborts bootstrap entirely (caught by the outer try/catch, notice shown, but
  // nothing ever renders). NOTE: `import 'core-js/stable'` above does NOT cover this —
  // confirmed babel-plugin-polyfill-corejs3@0.5.2 omits `es.promise.all-settled` from its
  // entry-injection output for this target even though its own compat data (safari: "13")
  // says it's needed; every other polyfill it injects (Array.flat/flatMap,
  // Object.fromEntries, Promise.finally, etc.) works correctly, just not this one.
  if (typeof Promise !== 'undefined' && !Promise.allSettled) {
    Promise.allSettled = function (promises) {
      return Promise.all(
        Array.prototype.map.call(promises, p =>
          Promise.resolve(p).then(
            value => ({ status: 'fulfilled', value }),
            reason => ({ status: 'rejected', reason })
          )
        )
      );
    };
  }

  const needsCore = !(window && window.Map && window.Set);
  const needsRaf = !('requestAnimationFrame' in window);
  const needsIntersectionObserver = typeof window.IntersectionObserver === 'undefined';
  // Intl.PluralRules is missing on Safari < 13 (11.1/High Sierra, 12.1/Mojave). Without it,
  // react-intl can't format any `{x, plural, …}` / `{x, selectordinal, …}` ICU message and
  // renders the raw ICU source instead (e.g. the order-breakdown line item). This MUST be
  // loaded before the first render — ensureLegacyPolyfills() gates render() below — so it is
  // awaited here (as a separate chunk, so modern browsers never download it) rather than in the
  // async ./util/polyfills chunk, which can arrive after first paint.
  const needsPluralRules = typeof Intl === 'undefined' || !Intl.PluralRules;

  const legacyCorePromise = needsCore || needsRaf ? import('./util/legacyPolyfills') : Promise.resolve();
  const pluralRulesPromise = needsPluralRules
    ? import('./util/intlPluralRulesPolyfill')
    : Promise.resolve();
  // IntersectionObserver is missing on Safari 11.1/High Sierra (added Safari 12.1 — Mojave and
  // everything newer already has the real thing). 11+ call sites across the app (search infinite
  // scroll, listing carousel, review tabs, PageBuilder, the sticky search bar's visibility
  // toggle, etc.) depend on it. A previous pass here used a hand-rolled shim that fired once
  // with `isIntersecting: true` and never again — fine for "lazy-load once visible" call sites,
  // but wrong for visibility *toggle* use cases (e.g. DesktopSearchBar's sticky-background
  // detection, which needs to see the "no longer intersecting" transition too). Using the W3C's
  // own reference polyfill instead, which correctly implements rootMargin/threshold and repeated
  // intersection-state changes — correct for every call site, not just the lazy-load ones.
  const intersectionObserverPromise = needsIntersectionObserver
    ? import('intersection-observer')
    : Promise.resolve();

  return Promise.all([legacyCorePromise, intersectionObserverPromise, pluralRulesPromise]);
};

// Load analytics when the browser is idle or after load
const loadAnalyticsIdle = () => (
  'requestIdleCallback' in window
    ? new Promise(resolve => requestIdleCallback(() => import('./analytics/analytics').then(resolve)))
    : new Promise(resolve => window.addEventListener('load', () => import('./analytics/analytics').then(resolve), { once: true }))
);

const FBQ_REFERENCE_ERROR_PATTERN = /(can't find variable:\s*fbq|fbq is not defined)/i;
const LOGROCKET_BOT_UA_PATTERN = /Googlebot|Bingbot|Slurp|DuckDuckBot|Baiduspider|YandexBot|Sogou|Exabot|facebot|ia_archiver/i;
const LOGROCKET_BOT_UA_PATTERN_2 = /bot|crawl|spider|google|bing|yahoo|facebook|slurp/i;
const RESIZE_OBSERVER_LOOP_PATTERN = /ResizeObserver loop/;

const suppressBenignTrackingScriptErrors = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.addEventListener(
    'error',
    event => {
      const message = event?.message || event?.error?.message || '';
      if (
        FBQ_REFERENCE_ERROR_PATTERN.test(message) ||
        RESIZE_OBSERVER_LOOP_PATTERN.test(message)
      ) {
        event.preventDefault?.();
        event.stopImmediatePropagation?.();
      }
    },
    true
  );
};

// Safety net for uncaught errors during app bootstrap (store/SDK setup, or the
// hydrate/render call itself). Without this, a failure here leaves the visitor
// stuck on the server-rendered shell with no indication anything went wrong —
// no auth state, no dynamic content, dead buttons, but no signal to the user.
// Appended alongside the SSR markup rather than replacing #root, so whatever
// server-rendered content is already visible stays visible.
const showBootstrapErrorNotice = () => {
  if (typeof document === 'undefined' || document.getElementById('bootstrap-error-notice')) {
    return;
  }
  const notice = document.createElement('div');
  notice.id = 'bootstrap-error-notice';
  notice.setAttribute('role', 'alert');
  notice.style.cssText =
    'position:fixed;bottom:0;left:0;right:0;z-index:2147483647;' +
    'background:#fff3cd;color:#664d03;border-top:1px solid #ffe69c;' +
    'font:14px/1.4 -apple-system,BlinkMacSystemFont,sans-serif;' +
    'padding:10px 16px;text-align:center;';
  notice.textContent =
    "Some parts of this page couldn't load. Please refresh, or update your browser if the problem continues.";
  if (document.body) {
    document.body.appendChild(notice);
  }
};


const render = (store, shouldHydrate) => {
  // If the client side server already loaded the auth information, render the app
  // immediately. Otherwise wait for the flag to be loaded and render
  // when auth information is present.
  const state = store.getState();
  const cdnAssetsVersion = state.hostedAssets.version;
  const authInfoLoaded = state.auth.authInfoLoaded;

  // OPTIMIZATION: Start non-blocking operations immediately
  const polyfillsPromise = loadPolyfills();
  const analyticsPromise = loadAnalyticsIdle();

  const info = authInfoLoaded ? Promise.resolve({}) : store.dispatch(authInfo());

  // OPTIMIZATION: Parallel execution of critical and non-critical operations
  Promise.all([
    info,
    loadableReady(),
    store.dispatch(fetchAppAssets(defaultConfig.appCdnAssets, cdnAssetsVersion)),
  ])
    .then(([_, __, fetchedAppAssets]) => {
      const { translations: translationsRaw, ...rest } = fetchedAppAssets || {};
      // We'll handle translations as a separate data.
      // It's given to React Intl instead of pushing to config Context
      const translations = translationsRaw?.data || {};

      // Rest of the assets are considered as hosted configs
      const configEntries = Object.entries(rest);
      const hostedConfig = configEntries.reduce((collectedData, [name, content]) => {
        return { ...collectedData, [name]: content.data || {} };
      }, {});

      // OPTIMIZATION: Render app immediately with available data
      if (shouldHydrate) {
        ReactDOM.hydrate(
          <ClientApp store={store} hostedTranslations={translations} hostedConfig={hostedConfig} />,
          document.getElementById('root')
        );
      } else {
        ReactDOM.render(
          <ClientApp store={store} hostedTranslations={translations} hostedConfig={hostedConfig} />,
          document.getElementById('root')
        );
      }

      // Detect app resume after a long background period (PWA on homescreen / mobile browser).
      // When the page was suspended 30+ seconds, the server connection may have been dropped.
      // Re-dispatching authInfo() re-establishes the session; if it fails, reload the page
      // so the user gets a clean state rather than a stuck error screen.
      let hiddenAt = null;
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          hiddenAt = Date.now();
        } else if (document.visibilityState === 'visible' && hiddenAt !== null) {
          const elapsed = Date.now() - hiddenAt;
          hiddenAt = null;
          if (elapsed > 30000) {
            store.dispatch(authInfo()).catch(() => window.location.reload());
          }
        }
      });

      // OPTIMIZATION: Load user data and cart in background after app is rendered
      // This prevents blocking the initial render
      const userDataPromise = store.dispatch(fetchCurrentUser()).then(currentUser => {
        // User's currency is being determined based on his country
        // if user is from an unsupported country, the deafult currency
        // should be USD.
        store.dispatch(setCUserCurrencyAndLocation(currentUser));
        store.dispatch(fetchCartItems());
        if (!!currentUser && !!currentUser.id) {
          //  initiateIntercomForLoggnedUser(currentUser);
        };
        return currentUser;
      }).catch(error => {
        // Silently handle user data loading errors to prevent app crashes
        console.warn('Failed to load user data:', error);
        return null;
      });

      // OPTIMIZATION: Load polyfills and analytics in parallel with user data
      return Promise.allSettled([
        userDataPromise,
        polyfillsPromise,
        analyticsPromise
      ]);
    })
    .then(([userResult, polyfillsResult, analyticsResult]) => {
      // OPTIMIZATION: Handle successful polyfill and analytics loading
      if (polyfillsResult.status === 'fulfilled') {
        console.log('Polyfills loaded successfully');
      }
      if (analyticsResult.status === 'fulfilled') {
        console.log('Analytics loaded successfully');
      }

    })
    .catch(e => {
      log.error(e, 'browser-side-render-failed');
      showBootstrapErrorNotice();
    });
};

const setupAnalyticsHandlers = googleAnalyticsId => {
  let handlers = [];

  // Log analytics page views and events in dev mode
  if (appSettings.dev) {
    handlers.push(new LoggingEventsHandler());
  }

  // Add EventsHandler for Algolia/Intercom/GA4 (GA4 calls are guarded by window.gtag)
  handlers.push(new EventsHandler());

  // Warn if GA4 measurement id is invalid
  if (googleAnalyticsId && googleAnalyticsId.indexOf('G-') !== 0) {
    console.warn(
      'Google Analytics 4 (GA4) should have measurement id that starts with "G-" prefix'
    );
  }

  return handlers;
};

// If we're in a browser already, render the client application.
if (typeof window !== 'undefined') {
  try {
    // Ignore known third-party fbq reference errors when tracking scripts are blocked.
    suppressBenignTrackingScriptErrors();

    // set up logger with Sentry DSN client key and environment
    log.setup();

    const baseUrl = appSettings.sdk.baseUrl ? { baseUrl: appSettings.sdk.baseUrl } : {};

    const assetCdnBaseUrl = appSettings.sdk.assetCdnBaseUrl
      ? { assetCdnBaseUrl: appSettings.sdk.assetCdnBaseUrl }
      : {};

    // eslint-disable-next-line no-underscore-dangle
    const preloadedState = window.__PRELOADED_STATE__ || '{}';
    const initialState = JSON.parse(preloadedState, sdkTypes.reviver);
    const sdk = createInstance({
      transitVerbose: appSettings.sdk.transitVerbose,
      clientId: appSettings.sdk.clientId,
      secure: appSettings.usingSSL,
      typeHandlers: apiUtils.typeHandlers,
      ...baseUrl,
      ...assetCdnBaseUrl,
    });

    // Note: on localhost:3000, you need to use environment variable.
    const googleAnalyticsIdFromSSR = initialState?.hostedAssets?.googleAnalyticsId;
    const googleAnalyticsId = googleAnalyticsIdFromSSR || process.env.REACT_APP_GOOGLE_ANALYTICS_ID;
    const analyticsHandlers = setupAnalyticsHandlers(googleAnalyticsId);
    const store = configureStore(initialState, sdk, analyticsHandlers);

    const initLogRocket = () => {
      const appId = process.env.REACT_APP_LOGROCKET_APP_ID;
      if (!appId || process.env.NODE_ENV !== 'production') {
        return Promise.resolve();
      }
      const userAgent = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
      if (LOGROCKET_BOT_UA_PATTERN.test(userAgent) || LOGROCKET_BOT_UA_PATTERN_2.test(userAgent)) {
        return Promise.resolve();
      }

      return import('logrocket').then(({ default: LogRocket }) => {
        LogRocket.init(appId);
        identifyCurrentUserToLogRocket(LogRocket, store);
      });
    };

    // Without this, every recorded session is anonymous: LogRocket cannot be searched by
    // customer, so tying a reported problem to its session means guessing from timestamps
    // and URLs. currentUser arrives asynchronously (and changes on login/logout), so watch
    // the store rather than reading it once at init.
    const identifyCurrentUserToLogRocket = (LogRocket, reduxStore) => {
      let identifiedUserId = null;
      const identify = () => {
        try {
          const { currentUser } = reduxStore.getState().user || {};
          const userId = currentUser?.id?.uuid;
          if (!userId || userId === identifiedUserId) {
            return;
          }
          identifiedUserId = userId;
          const { email, profile } = currentUser.attributes || {};
          LogRocket.identify(userId, {
            ...(email ? { email } : {}),
            ...(profile?.displayName ? { name: profile.displayName } : {}),
          });
        } catch (e) {
          // Session recording must never break the app.
        }
      };
      identify();
      reduxStore.subscribe(identify);
    };

    // Keep legacy polyfills as a pre-render guard, but don't block first paint on LogRocket.
    ensureLegacyPolyfills()
      .then(() => render(store, !!window.__PRELOADED_STATE__))
      .catch(() => render(store, !!window.__PRELOADED_STATE__))
      .finally(() => {
        initLogRocket().catch(() => {});
      });

    if (appSettings.dev) {
      // Expose stuff for the browser REPL
      window.app = {
        appSettings,
        defaultConfig,
        sdk,
        sdkTypes,
        store,
      };
    }
  } catch (e) {
    // Setup/bootstrap failed before render() could even be reached (e.g. bad
    // preloaded state, SDK construction error). Nothing else will render, so
    // surface it instead of leaving the visitor on a silent, static SSR shell.
    log.error(e, 'browser-side-bootstrap-failed');
    showBootstrapErrorNotice();
  }
}

// Show warning if CSP is not enabled
const CSP = process.env.REACT_APP_CSP;
const cspEnabled = CSP === 'block' || CSP === 'report';

if (CSP === 'report' && process.env.REACT_APP_ENV === 'production') {
  console.warn(
    'Your production environment should use CSP with "block" mode. Read more from: https://www.sharetribe.com/docs/ftw-security/how-to-set-up-csp-for-ftw/'
  );
} else if (!cspEnabled) {
  console.warn(
    "CSP is currently not enabled! You should add an environment variable REACT_APP_CSP with the value 'report' or 'block'. Read more from: https://www.sharetribe.com/docs/ftw-security/how-to-set-up-csp-for-ftw/"
  );
}

// Export the function for server side rendering.
export default renderApp;

// exporting matchPathname and configureStore for server side rendering.
// matchPathname helps to figure out which route is called and if it has preloading needs
// configureStore is used for creating initial store state for Redux after preloading
export {
  matchPathname,
  configureStore,
  routeConfiguration,
  defaultConfig,
  mergeConfig,
  fetchAppAssets,
};
